import getExtensionFromMimeType from '@/features/attachments/getExtensionFromMimeType';
import { resolveUrl } from '@/lib/api-utils';
import { AddAttachmentPayload } from '@certhon/models';
import { toast } from 'sonner';

/**
 * Download an attachment via a temporary link. The availability probe first
 * fetches the file (served from the SW cache when offline): a blind link
 * click would fail as a silent browser download error when the file can't
 * be served (offline and not cached — attachments are cached on view/upload,
 * never warmed). On failure the user gets a toast instead; the probe's
 * response lands in the SW cache, so the link click that follows is served
 * locally.
 */
export default async function downloadAttachment(
  attachment: AddAttachmentPayload,
) {
  const filename =
    attachment.filename ||
    `${attachment.checksum}.${getExtensionFromMimeType(attachment.contentType)}`;
  const url = resolveUrl(`/attachments/${attachment.checksum}`);

  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      toast.error(
        navigator.onLine
          ? 'Could not download this attachment'
          : 'This attachment is not available offline — try again when reconnected',
      );
      return;
    }
  } catch {
    toast.error(
      'This attachment is not available offline — try again when reconnected',
    );
    return;
  }

  // Create a direct link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  // Append to document, click, and cleanup
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
